<template>
  <v2-card>
    <template #header>
      <span class="v2-font-semibold v2-text-sm">
        {{ order.shipmentId ? 'Shipment' : 'Order' }} Summary
      </span>
    </template>
    <div class="v2-order-summary">
      <div class="v2-order-summary__row">
        <span class="v2-order-summary__label">Total Quantity:</span>
        <span class="v2-order-summary__value">{{ formatNumber(order.totalQuantity || 0) }}</span>
      </div>
      <div
        v-if="shouldDisplayCreditPoints(order)"
        class="v2-order-summary__row"
      >
        <span class="v2-order-summary__label">Applied credit points:</span>
        <span class="v2-order-summary__value">{{ formatCurrency(order.discountInCredit, currency(order)) }}</span>
      </div>
      <div class="v2-order-summary__row">
        <span class="v2-order-summary__label">Total Cost:</span>
        <span class="v2-order-summary__value">{{ formatCurrency(order.totalPrice || 0, currency(order)) }}</span>
      </div>
      <div class="v2-order-summary__row">
        <span class="v2-order-summary__label">Payment Terms:</span>
        <span class="v2-order-summary__value">{{ order.paymentTerms }}</span>
      </div>
      <div class="v2-order-summary__row">
        <span class="v2-order-summary__label">Logistic Terms:</span>
        <span class="v2-order-summary__value">{{ order.logisticsTerms }}</span>
      </div>
      <!-- Requested Delivery Window -->
      <div
        v-if="!order.shipmentId"
        class="v2-order-summary__row"
      >
        <span class="v2-order-summary__label">Requested Delivery Window:</span>
        <template v-if="!editRequestDeliveryDate">
          <span class="v2-order-summary__value">
            {{ requestedDeliveryDate || '-' }}
            <button
              v-if="isRetailer && canEditDate"
              class="v2-order-summary__edit-btn"
              @click="editRequestDeliveryDate = true"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </span>
        </template>
        <template v-if="isRetailer && editRequestDeliveryDate">
          <div class="v2-order-summary__date-edit">
            <v2-date-range-picker
              :value="requestDeliveryDate"
              :placeholder="KP_MONTH_FORMAT.PLACEHOLDER"
              :config="VUE_FLATPICKR_MONTH_CONFIG"
              @change="onDateInput"
            />
            <div
              v-if="changeDate"
              class="v2-order-summary__date-actions"
            >
              <v2-button
                size="sm"
                variant="primary"
                @click="handleUpdateAvailabilityDate"
              >
                Save
              </v2-button>
              <v2-button
                size="sm"
                variant="outline"
                @click="handleResetDate"
              >
                Cancel
              </v2-button>
            </div>
          </div>
        </template>
      </div>
      <!-- Estimated Delivery Window -->
      <div
        v-if="!order.shipmentId"
        class="v2-order-summary__row"
      >
        <span class="v2-order-summary__label">Estimated Delivery Window:</span>
        <template v-if="!onDisplayCalender">
          <span class="v2-order-summary__value">
            {{ expectedAvailabilityDate || '-' }}
            <button
              v-if="isBrand && canEditDate"
              class="v2-order-summary__edit-btn"
              @click="handleEditExpectedDate"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </span>
        </template>
        <template v-if="isBrand && onDisplayCalender">
          <div class="v2-order-summary__date-edit">
            <v2-date-range-picker
              :value="expectedAvailabilityDate"
              :placeholder="KP_MONTH_FORMAT.PLACEHOLDER"
              :config="VUE_FLATPICKR_MONTH_CONFIG"
              @change="handleUpdateExpectedDate"
            />
          </div>
        </template>
      </div>
      <!-- Discount -->
      <div
        v-if="isDiscountVisible(order)"
        class="v2-order-summary__row"
      >
        <span class="v2-order-summary__label">Discount:</span>
        <template v-if="!isEditDiscount">
          <span class="v2-order-summary__value">
            {{ discount }}%
            <button
              v-if="isBrand"
              class="v2-order-summary__edit-btn"
              @click="isEditDiscount = true"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </span>
        </template>
        <template v-if="isEditDiscount">
          <div class="v2-order-summary__discount-edit">
            <v2-input
              v-model="discount"
              type="number"
              placeholder="Discount"
            />
            <v2-button
              size="sm"
              variant="primary"
              @click="handleDiscountSave"
            >
              Save
            </v2-button>
          </div>
        </template>
      </div>
    </div>
    <!-- Confirm discount modal -->
    <v2-modal
      :show="showDiscountModal"
      title="Update Order Discount"
      @close="cancelDiscountUpdate"
    >
      <p>This discount will apply to the entire order and remove any product-level discounts.</p>
      <p>Do you want to continue?</p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="cancelDiscountUpdate"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          @click="confirmUpdateDiscount"
        >
          Confirm
        </v2-button>
      </template>
    </v2-modal>
  </v2-card>
</template>

<script>
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2DateRangePicker from '@/components/v2/V2DateRangePicker.vue'
import { useOrderSummary } from '@/composables/useOrderSummary'
import { VUE_FLATPICKR_MONTH_CONFIG } from '@/constants'

export default {
  name: 'V2OrderSummary',
  components: {
    V2Card,
    V2Button,
    V2Input,
    V2Modal,
    V2DateRangePicker,
  },
  props: {
    order: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const summary = useOrderSummary()
    summary.initOrderSummary(props.order)
    return { ...summary }
  },
  data() {
    return {
      showDiscountModal: false,
      VUE_FLATPICKR_MONTH_CONFIG,
    }
  },
  methods: {
    handleEditExpectedDate() {
      this.onDisplayCalender = true
    },
    onDateInput(val) {
      // V2DateRangePicker emits flatpickr's selectedDates (an array of Date objects).
      // Convert to the "MM/YYYY" string the rest of this flow expects. Storing the raw
      // array fed an array back into the picker's :value, which made flat-pickr re-trigger
      // on-change endlessly (array never equals the input's string value) and froze the UI.
      const selectedDate = Array.isArray(val) ? val[0] : val
      if (!selectedDate) return
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
      const year = selectedDate.getFullYear()
      const displayDate = `${month}/${year}`
      this.requestDeliveryDate = displayDate
      this.onRequestedDeliveryDateChange(displayDate)
    },
    handleUpdateAvailabilityDate() {
      this.onUpdateAvailabilityDate(this.order._id)
        .then(() => {
          this.order.requestedDeliveryDate = this.requestDeliveryDate
        })
    },
    handleResetDate() {
      this.resetDate(this.order.requestedDeliveryDate)
    },
    handleUpdateExpectedDate(dates) {
      this.onUpdateExpectedAvailabilityDate(dates, this.order._id)
    },
    handleDiscountSave() {
      if (this.hasAnyProductDiscount(this.order.products)) {
        this.showDiscountModal = true
      } else {
        this.submitDiscount()
      }
    },
    cancelDiscountUpdate() {
      this.showDiscountModal = false
      this.discount = this.order.discount
      this.isEditDiscount = false
    },
    confirmUpdateDiscount() {
      this.showDiscountModal = false
      this.submitDiscount()
    },
    submitDiscount() {
      this.onUpdateDiscount(this.order._id).then(result => {
        if (result?.success) {
          this.$emit('load-order')
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-order-summary {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    font-size: 0.8125rem;
  }

  &__label {
    color: var(--v2-muted-foreground);
    white-space: nowrap;
  }

  &__value {
    font-weight: 600;
    color: var(--v2-foreground);
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  &__edit-btn {
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: var(--v2-muted-foreground);
    display: inline-flex;
    align-items: center;

    &:hover {
      color: var(--v2-primary);
    }
  }

  &__date-edit {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  &__date-actions {
    display: flex;
    gap: 0.5rem;
  }

  &__discount-edit {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>
